import { AsyncPipe } from '@angular/common'
import type { OnDestroy, OnInit } from '@angular/core'
import { Component, HostBinding, NgZone, inject, signal } from '@angular/core'
import { Router, RouterLink } from '@angular/router'

import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { InfiniteScrollDirective } from 'ngx-infinite-scroll'
import {
  BehaviorSubject,
  Subject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  takeUntil,
} from 'rxjs'

import { EditorsService } from '../../../../../api/editors-service/editors.service'
import type {
  EditorDto,
  EditorsResponseDto,
} from '../../../../../api/responses/editors/editors-response.dto'
import type { SortOrder } from '../../../../../types/types'
import { EditorItemComponent } from '../editor-item/editor-item.component'

interface EditorWithType extends Omit<EditorDto, 'type'> {
  type: keyof EditorsResponseDto['editors']
}

interface EditorGroup {
  type: string
  translationKey: string
  editors: EditorWithType[]
}

@Component({
  selector: 'dhp-editors-list',
  templateUrl: './editors-list.component.html',
  styleUrls: ['./editors-list.component.scss'],
  imports: [
    InfiniteScrollDirective,
    RouterLink,
    EditorItemComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class EditorsListComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router)
  private readonly editorsService = inject(EditorsService)
  private readonly translateService = inject(TranslateService)
  private readonly ngZone = inject(NgZone)

  @HostBinding('attr.ngSkipHydration') ngSkipHydration = true

  private readonly searchTerm$ = new BehaviorSubject<string>('')
  readonly editors$ = new BehaviorSubject<EditorWithType[]>([])
  readonly editorsGrouped$ = new BehaviorSubject<EditorGroup[]>([])
  loading$ = new BehaviorSubject<boolean>(false)
  readonly error = signal(false)
  isLoadingMore$ = new BehaviorSubject<boolean>(false)
  currentPage = 1
  readonly itemsPerPage = 20
  readonly hasMoreItems = signal(true)
  readonly totalItems = signal(0)
  private destroy$ = new Subject<void>()

  readonly sortOrder = signal<SortOrder>('asc')
  readonly showSortDropdown = signal(false)

  private typeMappings: {
    key: keyof EditorsResponseDto['editors']
    translationKey: string
  }[]

  constructor() {
    this.typeMappings = [
      { key: 'chiefEditor', translationKey: 'pages.editors.chiefEditor' },
      { key: 'redaction', translationKey: 'pages.editors.editor' },
      {
        key: 'editorialSecretary',
        translationKey: 'pages.editors.editorialSecretary',
      },
      {
        key: 'permanentEmployees',
        translationKey: 'pages.editors.permanentEmployee',
      },
      { key: 'users', translationKey: 'pages.editors.users' },
    ]
  }

  ngOnInit(): void {
    // Combine search term with editors for filtering
    combineLatest([
      this.searchTerm$.pipe(debounceTime(300), distinctUntilChanged()),
      this.editors$,
    ])
      .pipe(
        takeUntil(this.destroy$),
        map(([term, editors]) => {
          if (!term) return editors
          const lowerTerm = term.toLowerCase()
          return editors.filter(editor =>
            this.matchesSearchTerm(editor, lowerTerm)
          )
        }),
        map(filteredEditors => {
          this.totalItems.set(filteredEditors.length)
          return this.createEditorGroups(filteredEditors)
        })
      )
      .subscribe(groups => {
        this.editorsGrouped$.next(groups)
      })

    this.loadEditors()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  set searchTerm(term: string) {
    this.searchTerm$.next(term)
  }

  get searchTerm(): string {
    return this.searchTerm$.value
  }

  resetAndReload(): void {
    this.editors$.next([])
    this.currentPage = 1
    this.hasMoreItems.set(true)
    this.loadEditors()
  }

  toggleSortDropdown(): void {
    this.showSortDropdown.set(!this.showSortDropdown())
  }

  setSortOrder(order: SortOrder): void {
    if (this.sortOrder() !== order) {
      this.sortOrder.set(order)
      this.resetAndReload()
    }
    this.showSortDropdown.set(false)
  }

  loadEditors(): void {
    this.loading$.next(true)
    this.error.set(false)
    this.editorsGrouped$.next([])

    this.editorsService
      .getEditors()
      .pipe(
        takeUntil(this.destroy$),
        map((response: EditorsResponseDto) => {
          // Combine all editors into a single array, preserving their type
          const allEditors = this.typeMappings.reduce<EditorWithType[]>(
            (acc, mapping) => {
              const editorsOfType = (response.editors[mapping.key] ?? []).map(
                editor => ({
                  ...editor,
                  type: mapping.key,
                })
              )
              return [...acc, ...editorsOfType]
            },
            []
          )

          // Update both editors$ and editorsGrouped$
          this.totalItems.set(allEditors.length)
          this.editors$.next(allEditors)
          this.editorsGrouped$.next(this.createEditorGroups(allEditors))
          return allEditors
        })
      )
      .subscribe({
        next: () => {
          this.loading$.next(false)
        },
        error: () => {
          this.error.set(true)
          this.loading$.next(false)
        },
      })
  }

  private matchesSearchTerm(editor: EditorWithType, term: string): boolean {
    return (
      (editor.first_name ?? '').toLowerCase().includes(term) ||
      (editor.last_name ?? '').toLowerCase().includes(term) ||
      (editor.company_name ?? '').toLowerCase().includes(term) ||
      (editor.post_title ?? '').toLowerCase().includes(term)
    )
  }

  onScroll(): void {
    this.ngZone.run(() => {
      if (this.isLoadingMore$.value || this.loading$.value) {
        return
      }

      if (!this.hasMoreItems()) {
        return
      }

      this.loadMore()
    })
  }

  private loadMore(): void {
    if (this.isLoadingMore$.value || !this.hasMoreItems()) return

    this.isLoadingMore$.next(true)
    this.currentPage++

    const start = (this.currentPage - 1) * this.itemsPerPage
    const end = start + this.itemsPerPage
    const remainingEditors = this.editors$.value.slice(start, end)

    if (remainingEditors.length > 0) {
      this.hasMoreItems.set(remainingEditors.length >= this.itemsPerPage)
    } else {
      this.hasMoreItems.set(false)
    }

    this.isLoadingMore$.next(false)
  }

  trackByEditorId(index: number, editor: EditorWithType): number | undefined {
    return editor.id
  }

  private createEditorGroups(editors: EditorWithType[]): EditorGroup[] {
    // Sort editors based on sortOrder
    const sortedEditors = [...editors].sort((a, b) => {
      const compareResult = (a.last_name ?? '').localeCompare(b.last_name ?? '')
      return this.sortOrder() === 'asc' ? compareResult : -compareResult
    })

    // Create groups based on editor types
    return this.typeMappings
      .map(mapping => ({
        type: mapping.key,
        translationKey: mapping.translationKey,
        editors: sortedEditors.filter(editor => editor.type === mapping.key),
      }))
      .filter(group => group.editors.length > 0)
  }
}
